import {
  GET,
  POST,
  PATCH,
  PUT,
  DELETE,
  POST_FORM,
  PATCH_FORM,
  Axios, // ✅ IMPORTANTE: Importar Axios explícitamente desde config
} from "../config";

// Endpoints básicos de sucursales
export const list = async (page = 1, filters = {}) => {
  let url = `branches/?page=${page}`;
  if (filters.page_size) {
    url += `&page_size=${filters.page_size}`;
  }
  const response = await GET(url);
  return response.data;
};

export const retrieve = async (id) => {
  const response = await GET(`branches/${id}/`);
  return response.data;
};

export const create = async (data) => {
  // Verificar si hay archivos en los datos
  const hasFiles = Object.values(data).some(
    (value) => value instanceof File || value instanceof Blob
  );

  if (hasFiles) {
    const response = await POST_FORM("branches/", data);
    return response.data;
  } else {
    const response = await POST("branches/", data);
    return response.data;
  }
};

export const update = async (id, data) => {
  // Verificar si hay archivos en los datos
  const hasFiles = Object.values(data).some(
    (value) => value instanceof File || value instanceof Blob
  );

  if (hasFiles) {
    const response = await PATCH_FORM(`branches/${id}/`, data);
    return response.data;
  } else {
    const response = await PATCH(`branches/${id}/`, data);
    return response.data;
  }
};

export const destroy = async (id) => {
  const response = await DELETE(`branches/${id}/`);
  return response.data;
};

// Gestión de sucursales del usuario
export const my_branches = async () => {
  const response = await GET("branches/my-branches/");
  return response.data;
};

export const my_branches_for_filters = async () => {
  const response = await GET("branches/my-branches-for-filters/");
  return response.data;
};

export const my_branches_select = async () => {
  try {
    const response = await GET("branches/my-branches-select/");
    return response.data;
  } catch (error) {
    // Endpoint opcional: si no existe, retornar arreglo vacío para no romper flujos
    if (error.response?.status === 404) {
      return [];
    }
    throw error;
  }
};

export const leave_branch = async (branchId) => {
  const response = await POST(`branches/${branchId}/leave-branch/`);
  return response.data;
};

export const transfer_ownership = async (branchId, newOwnerId) => {
  const response = await POST(`branches/${branchId}/transfer-ownership/`, {
    new_owner_id: newOwnerId,
  });
  return response.data;
};

// Gestión de invitaciones
export const my_invitations = async () => {
  const response = await GET("branches/my-invitations/");
  return response.data;
};

export const accept_invitation = async (branchId) => {
  const response = await POST(`branches/${branchId}/accept-invitation/`);
  return response.data;
};

export const reject_invitation = async (branchId) => {
  const response = await POST(`branches/${branchId}/reject-invitation/`);
  return response.data;
};

// Gestión de usuarios en sucursales
export const get_branch_users = async (branchId) => {
  const response = await GET(`branches/${branchId}/users/`);
  return response.data;
};

export const invite_user = async (branchId, data) => {
  const response = await POST(`branches/${branchId}/invite-user/`, data);
  return response.data;
};

export const update_user_role = async (branchId, data) => {
  const response = await PUT(`branches/${branchId}/update-user-role/`, data);
  return response.data;
};

export const remove_user = async (branchId, userId) => {
  const response = await DELETE(
    `branches/${branchId}/remove-user/?user_id=${userId}`
  );
  return response.data;
};

export const toggle_user_status = async (branchId, userId) => {
  const response = await PUT(`branches/${branchId}/toggle_user_status/`, {
    user_id: userId,
  });
  return response.data;
};

export const update_user_details = async (branchId, data) => {
  const response = await PUT(`branches/${branchId}/update-user-details/`, data);
  return response.data;
};

export const change_user_branch = async (
  branchId,
  userId,
  newBranchId,
  newRole = null
) => {
  const payload = {
    user_id: userId,
    new_branch_id: newBranchId,
  };

  // Only add new_role if it's provided
  if (newRole) {
    payload.new_role = newRole;
  }

  const response = await POST(
    `branches/${branchId}/change-user-branch/`,
    payload
  );
  return response.data;
};

// Remover usuario de sucursal (dejar sin asignación)
export const remove_user_from_branch = async (branchId, userId) => {
  const response = await DELETE(`branches/${branchId}/remove-user/`, {
    user_id: userId,
  });
  return response.data;
};

// Gestión de usuarios disponibles
export const get_available_users = async (branchId) => {
  const response = await GET(`branches/${branchId}/available-users/`);
  return response.data;
};

export const assign_existing_user = async (branchId, data) => {
  const response = await POST(
    `branches/${branchId}/users/assign/`,
    data
  );
  return response.data;
};

// Aprobar roles pendientes
export const approve_pending_roles = async () => {
  const response = await POST("branches/approve-pending-roles/");
  return response.data;
};

/**
 * Obtiene los roles habilitados para una sucursal.
 * @param {number} branchId - ID de la sucursal
 * @returns {Promise} - Respuesta con lista de roles y su estado
 */
export const getBranchRoles = async (branchId) => {
  const response = await GET(`branches/${branchId}/roles/`);
  return response.data;
};

/**
 * Actualiza los roles habilitados para una sucursal.
 * @param {number} branchId - ID de la sucursal
 * @param {Array} roles - Array de roles con su estado (is_enabled)
 * @returns {Promise} - Respuesta de la operación
 */
export const updateBranchRoles = async (branchId, roles) => {
  const response = await POST(`branches/${branchId}/roles/`, { roles });
  return response.data;
};

/**
 * Obtener configuración temática de una sucursal
 * @param {number} branchId - ID de la sucursal
 * @returns {Promise} - Configuración temática o null si no existe (404)
 */
export const getBranchThemeConfig = async (branchId) => {
  try {
    const response = await GET(`branches/${branchId}/theme-config/`);
    return response.data;
  } catch (error) {
    // Si el endpoint no existe (404), retornar null silenciosamente
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

/**
 * Actualizar configuración temática de una sucursal (solo propietario)
 * @param {number} branchId - ID de la sucursal
 * @param {Object} config - Datos de configuración temática
 * @returns {Promise} - Configuración actualizada
 */
export const updateBranchThemeConfig = async (branchId, config) => {
  // Verificar si hay archivos en los datos
  const hasFiles = Object.values(config).some(
    (value) => value instanceof File || value instanceof Blob
  );

  if (hasFiles) {
    const response = await PATCH_FORM(`branches/${branchId}/theme-config/`, config);
    return response.data;
  } else {
    const response = await PATCH(`branches/${branchId}/theme-config/`, config);
    return response.data;
  }
};

/**
 * Subir logo de sponsor.
 * @param {number} branchId - ID de la sucursal
 * @param {File} file - Archivo de imagen
 * @returns {Promise} - { url: string }
 */
export const uploadSponsorLogo = async (branchId, file) => {
  const response = await POST_FORM(`branches/${branchId}/upload-sponsor-logo/`, {
    file: file,
  });
  return response.data;
};

/**
 * Establecer una sucursal como predeterminada para el usuario actual
 * @param {number} branchId - ID de la sucursal
 * @returns {Promise} - Respuesta con configuración temática actualizada
 */
export const setBranchAsDefault = async (branchId) => {
  const response = await PATCH(`branches/${branchId}/set_default/`);
  return response.data;
};

/**
 * Obtiene la configuración de módulos por rol para una sucursal.
 * @param {number} branchId - ID de la sucursal
 * @returns {Promise} - Configuración de módulos por rol
 */
export const getRoleModules = async (branchId) => {
  const response = await GET(`branches/${branchId}/role-modules/`);
  return response.data;
};

/**
 * Configura módulos personalizados para un rol en una sucursal.
 * @param {number} branchId - ID de la sucursal
 * @param {string} role - Rol a configurar
 * @param {Object} modulesConfig - Configuración de módulos {enabled: [], extensions: [], read_only: []}
 * @returns {Promise} - Respuesta de la operación
 */
export const configureRoleModules = async (branchId, role, modulesConfig) => {
  const response = await POST(`branches/${branchId}/configure-role-modules/`, {
    role,
    modules_config: modulesConfig,
  });
  return response.data;
};

/**
 * Establecer sucursal activa en la sesión del usuario
 * ✅ NUEVO: Permite cambiar la sucursal de trabajo actual
 * @param {number} branchId - ID de la sucursal a activar
 * @returns {Promise} - Respuesta con datos de la sucursal activada y tema
 */
export const setActiveBranch = async (branchId) => {
  const response = await POST(`branches/${branchId}/set-active-branch/`);
  return response.data;
};

// ============================================
// ENDPOINTS PÚBLICOS (SIN AUTENTICACIÓN)
// Para login dinámico por sucursal
// ============================================

/**
 * Obtener configuración de tema para login por slug de sucursal.
 * ENDPOINT PÚBLICO - No requiere autenticación.
 * 
 * @param {string} slug - Slug único de la sucursal (ej: 'mi-empresa')
 * @returns {Promise} - Configuración de tema para la página de login
 */
export const getPublicLoginTheme = async (slug) => {
  // Usamos Axios con la opción skipAuth para evitar inyección de token
  // Esto es más robusto que fetch porque mantiene la configuración base y manejo de errores
  const response = await Axios.get(`branches/public-login-theme/${slug}/`, {
    skipAuth: true
  });
  return response.data;
};

/**
 * Listar todas las sucursales disponibles para login.
 * ENDPOINT PÚBLICO - No requiere autenticación.
 * 
 * @returns {Promise} - Lista de sucursales con login dinámico habilitado
 */
export const listPublicLoginBranches = async () => {
  const response = await GET(`branches/public-login-theme/`);
  return response.data;
};

// ============================================
// CONFIGURACIÓN POS POR SUCURSAL
// ============================================

/**
 * Obtener configuración POS de una sucursal.
 * @param {number} branchId - ID de la sucursal
 * @returns {Promise} - Configuración POS de la sucursal
 */
export const getBranchPOSConfig = async (branchId) => {
  const response = await GET(`branches/${branchId}/pos-config/`);
  return response.data;
};

/**
 * Actualizar configuración POS de una sucursal.
 * @param {number} branchId - ID de la sucursal
 * @param {Object} config - Datos de configuración POS
 * @returns {Promise} - Configuración actualizada
 */
export const updateBranchPOSConfig = async (branchId, config) => {
  const response = await PUT(`branches/${branchId}/pos-config/`, config);
  return response.data;
};
